<script lang="ts">
import AppSelect from '@/components/v2/AppSelect.vue';

export default {
  name: 'CreateTowerModal',
  components: {
    AppSelect,
  },
};
</script>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { accountService, programService } from '@/services/api';
import { AccountSummary } from '@/types/AccountSummary';
import { Program } from '@/types/Programs';
import z, { SafeParseError, ZodFormattedError } from 'zod';
import { ProgramTower } from '@/types/tower/v2/Tower';
import { towerService } from '@/services/v2/TowerService';
import {
  BOUND_COVERAGE_TOOLTIP,
  NEW_COVERAGE_TOOLTIP,
  RENEWAL_TOOLTIP,
  SCRATCH_TOOLTIP,
  TowerType,
} from '@/views/tower_dashboard/types/tower-dashboard';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import Combobox from '@/components/v2/Combobox.vue';
import { SelectOption } from '@/types/components/Select.js';

const props = defineProps<{
  towerType: TowerType;
  accountId?: string;
  programId?: string;
  show: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'create', towerId: string): void;
}>();

const formSchema = z.object({
  accountId: z.string(),
  programId: z.string(),
  versionLabel: z.string().min(1).max(100),
  towerName: z.string().optional(),
  renewalTowerId: z.string().optional(),
});

type FormSchemeType = z.infer<typeof formSchema>;

type ValidFormFields = {
  [K in keyof FormSchemeType]: boolean;
};

const defaultValidFormFields: ValidFormFields = {
  accountId: true,
  programId: true,
  versionLabel: true,
  towerName: true,
  renewalTowerId: true,
};

class CreateTowerForm {
  accountId: string = '';
  programId: string = '';
  versionLabel?: string;
  towerName?: string;
  renewalTowerId?: string;
}

// ----------------
// refs and consts
// ----------------
const form = ref<CreateTowerForm>(new CreateTowerForm());
const formErrors = ref<ZodFormattedError<FormSchemeType>>();
const validFormFields = ref<ValidFormFields>(structuredClone(defaultValidFormFields));
const accounts = ref<AccountSummary[]>([]);
const programs = ref<Program[]>([]);
const towerVersions = ref<ProgramTower[]>([]);
const noRenewalVersions = ref(false);
const clients = ref<SelectOption[]>([]);
const programOptions = ref<SelectOption[]>([]);

// ---------
// lifecycles
// ---------
onMounted(async () => {
  addEventListener('keyup', handleCloseWithEscapeKey);

  accounts.value = await accountService.list();

  clients.value = accounts.value.map((account) => {
    return {
      label: account.accountName,
      value: account.accountID.toString(),
    };
  });
});

onUnmounted(() => {
  removeEventListener('keyup', handleCloseWithEscapeKey);
});

// ---------
// watchers
// ---------
watch(
  () => form.value.accountId,
  async () => {
    if (Number(form.value.accountId) && [TowerType.BoundCoverage, TowerType.Renewal].includes(props.towerType)) {
      programs.value = await accountService.getProgramsForAccount(Number(form.value.accountId), true);
      programs.value = programs.value.map((program) => {
        if (program.type === 'Manual') {
          program.name = `${program.year} ${program.name}`;
        }
        return program;
      });

      // group by year
      const programsByYear = programs.value.reduce((acc, program) => {
        if (!acc.has(program.year)) {
          acc.set(program.year, []);
        }
        acc.get(program.year)?.push(program);
        return acc;
      }, new Map<number, Program[]>());

      // sort each group separately
      programsByYear.forEach((programs) => {
        programs.sort((a, b) => a.name.localeCompare(b.name));
      });

      // sort years descending, then combine the groups
      programs.value = Array.from(programsByYear.keys())
        .sort((y1, y2) => y2 - y1)
        .reduce((acc, yearKey) => {
          acc.push(...(programsByYear.get(yearKey) ?? []));
          return acc;
        }, [] as Program[]);

      // Create program options from the sorted programs
      programOptions.value = programs.value.map((program) => ({
        label: program.name,
        value: program.id.toString(),
      }));
    }
  },
);

watch(
  () => form.value.programId,
  async () => {
    if (Number(form.value.accountId) && Number(form.value.programId) && props.towerType === TowerType.Renewal) {
      towerVersions.value = await towerService.getTowers({
        accountId: Number(form.value.accountId),
        programId: Number(form.value.programId),
      });

      if (towerVersions.value.length === 0) {
        noRenewalVersions.value = true;
        form.value.versionLabel = '';
      } else {
        noRenewalVersions.value = false;
      }
    }
  },
);

watch(
  () => props.show,
  (show) => {
    if (show) {
      switch (props.towerType) {
        case TowerType.NewCoverage:
          form.value.accountId = props.accountId ?? '';
          form.value.programId = '';
          break;
        case TowerType.Scratch:
          form.value.accountId = '';
          form.value.programId = '';
          break;
        case TowerType.Renewal:
          form.value.versionLabel = 'Renewal';
          form.value.accountId = props.accountId ?? '';
          form.value.programId = props.programId ?? '';
          break;
        case TowerType.BoundCoverage:
          form.value.accountId = props.accountId ?? '';
          form.value.programId = props.programId ?? '';
          break;
      }
    } else {
      form.value = new CreateTowerForm();
    }
  },
);

// ---------
// computed
// ---------
const towerTypeName = computed(() => {
  switch (props.towerType) {
    case TowerType.BoundCoverage:
      return 'Bound Coverage';

    case TowerType.NewCoverage:
      return 'New Coverage';

    case TowerType.Renewal:
      return 'Renewal';

    case TowerType.Scratch:
      return 'Scratch';

    default:
      return '';
  }
});

const towerTypeDescription = computed(() => {
  switch (props.towerType) {
    case TowerType.BoundCoverage:
      return BOUND_COVERAGE_TOOLTIP;

    case TowerType.NewCoverage:
      return NEW_COVERAGE_TOOLTIP;

    case TowerType.Renewal:
      return RENEWAL_TOOLTIP;

    case TowerType.Scratch:
      return SCRATCH_TOOLTIP;

    default:
      return '';
  }
});

const showVersionName = computed(() => {
  return !(props.towerType === TowerType.Renewal) || (props.towerType === TowerType.Renewal && noRenewalVersions.value);
});

const isAccountIdProvided = computed<boolean>(() => {
  return !!(props.accountId && props.accountId);
});

const isProgramIdProvided = computed<boolean>(() => {
  return !!(props.programId && props.programId);
});

// ---------
// handlers
// ---------
function handleCloseWithEscapeKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    handleClose();
  }
}

function handleClose(): void {
  emit('close');
}

async function handleCreate(): Promise<void> {
  try {
    // validate the form
    if (!validateForm()) {
      console.log('Failed to create tower');
      return;
    }

    let towerObject = '{}';
    let programId = Number(form.value.programId);
    let towerName = form.value.towerName;
    let versionLabel = form.value.versionLabel;
    let renewalProgramId: number | undefined = undefined;

    // renewal towers will copy the tower object from the selected tower
    if (props.towerType === TowerType.Renewal) {
      if (programId) {
        const programInfo = await programService.getProgramInfoById(programId);

        towerName = 'Renewal of ' + programInfo.program.name;
        renewalProgramId = programInfo.program.id;
      }

      if (form.value.renewalTowerId !== undefined) {
        const towers = await towerService.getTowers({ towerIds: [form.value.renewalTowerId] });
        if (towers && towers.length > 0) {
          towerObject = towers[0].towerObject;

          if (towers[0].versionLabel) {
            versionLabel = towers[0].versionLabel;
          }
        }
      }

      // renewals are not associated with a program
      programId = 0;
    }

    const tower: Partial<ProgramTower> = {
      accountId: Number(form.value.accountId),
      programId: programId,
      renewalProgramId: renewalProgramId,
      versionLabel: versionLabel,
      towerName: towerName,
      towerTypeId: props.towerType,
      towerObject: towerObject,
    };

    // save the tower
    const response = await towerService.createTower(tower);
    emit('create', response.towerId);
  } catch (e) {
    console.error('tower create error', e);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Error: Failed to create tower', color: 'error' });
  }
}

// private functions
function validateForm(): boolean {
  const valid = formSchema.safeParse(form.value);
  validFormFields.value = structuredClone(defaultValidFormFields);

  if (!valid.success) {
    // set valid form fields to false
    formErrors.value = valid.error.format();

    for (const error of (valid as SafeParseError<FormSchemeType>).error.issues) {
      validFormFields.value[error.path[0].toString() as keyof ValidFormFields] = false;
    }
  }

  return valid.success;
}
</script>

<template>
  <dialog class="tw:modal" :class="{ 'tw:modal-open': show }">
    <div class="tw:form-control tw:modal-box tw:flex tw:flex-col tw:gap-4">
      <div class="tw:flex tw:flex-col">
        <h1>Create {{ towerTypeName }} Tower</h1>
        <p class="tw:px-5 tw:pt-2 tw:text-xs tw:italic">{{ towerTypeDescription }}</p>
      </div>

      <!-- Account Dropdown-->
      <div
        v-if="[TowerType.BoundCoverage, TowerType.NewCoverage, TowerType.Renewal].includes(props.towerType)"
        class="tw:w-full">
        <label class="tw:label">
          <span class="tw:label-text tw:text-xs tw:text-gray-500">Account <span class="tw:text-primary">*</span></span>
        </label>
        <Combobox
          class="tw:w-full"
          data-testid="company-name"
          v-model="form.accountId"
          :disabled="isAccountIdProvided"
          :options="clients" />
        <span v-show="formErrors?.accountId?._errors" class="tw:label-text-alt tw:text-xs tw:text-red-500">
          {{ formErrors?.accountId?._errors.join(', ') }}
        </span>
      </div>

      <!-- Program Dropdown -->
      <div v-if="[TowerType.BoundCoverage, TowerType.Renewal].includes(props.towerType)" class="tw:w-full">
        <label class="tw:label">
          <span class="tw:label-text tw:text-xs tw:text-gray-500">Program <span class="tw:text-primary">*</span></span>
        </label>
        <Combobox
          v-model="form.programId"
          :options="programOptions"
          class="tw:w-full"
          :disabled="programs.length === 0 || isProgramIdProvided"
          :input-class="formErrors?.programId?._errors ? 'tw:h-12 tw:border-red-500' : 'tw:h-12'" />
        <span v-visible="formErrors?.programId?._errors" class="tw:label-text-alt tw:text-xs tw:text-red-500">
          {{ formErrors?.programId?._errors.join(', ') }}
        </span>
      </div>

      <!-- Tower Name -->
      <div v-if="[TowerType.NewCoverage, TowerType.Scratch].includes(props.towerType)" class="tw:w-full">
        <label class="tw:label">
          <span class="tw:label-text tw:text-xs tw:text-gray-500">Tower Name</span>
        </label>
        <input
          type="text"
          placeholder="e.g. Proposal for D&O"
          v-model="form.towerName"
          class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
          :class="{ 'tw:input-error': formErrors?.towerName?._errors }" />
        <label class="tw:label">
          <span v-show="formErrors?.towerName?._errors" class="tw:label-text-alt tw:text-xs tw:text-red-500">
            {{ formErrors?.towerName?._errors.join(', ') }}
          </span>
        </label>
      </div>

      <!-- Version Name -->
      <div v-if="showVersionName" class="tw:w-full">
        <label class="tw:label">
          <span class="tw:label-text tw:text-xs tw:text-gray-500"
            >Version Name <span class="tw:text-primary">*</span></span
          >
        </label>
        <input
          type="text"
          placeholder="e.g. Version with Side A"
          v-model="form.versionLabel"
          class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
          :class="{ 'tw:input-error': formErrors?.versionLabel?._errors }" />
        <label class="tw:label">
          <span v-show="formErrors?.versionLabel?._errors" class="tw:label-text-alt tw:text-xs tw:text-red-500">
            {{ formErrors?.versionLabel?._errors.join(', ') }}
          </span>
        </label>
      </div>

      <!-- Version Dropdown (for Renewal) -->
      <div v-if="[TowerType.Renewal].includes(props.towerType) && !noRenewalVersions" class="tw:w-full">
        <label class="tw:label">
          <span class="tw:label-text tw:text-xs tw:text-gray-500">Tower Version</span>
        </label>
        <AppSelect
          v-model="form.renewalTowerId"
          :options="
            towerVersions.map((tower) => ({
              label: tower.versionLabel || '',
              value: tower.towerId,
            }))
          "
          class="tw:mb-4 tw:h-12 tw:w-full"
          :disabled="towerVersions.length === 0"
          :error="formErrors?.renewalTowerId?._errors !== undefined" />
        <span v-show="formErrors?.renewalTowerId?._errors" class="tw:label-text-alt tw:text-xs tw:text-red-500">
          {{ formErrors?.renewalTowerId?._errors.join(', ') }}
        </span>
      </div>

      <div class="tw:modal-action">
        <button class="tw:btn tw:btn-sm" @click="handleClose">Cancel</button>
        <button class="tw:btn tw:text-white tw:btn-sm tw:btn-primary" @click="handleCreate">Create</button>
      </div>
    </div>
  </dialog>
</template>

<style></style>
